import { createSlice } from '@reduxjs/toolkit';
import { pushDynamicTab } from '../utils/dynamicTabs';

// 🔹 Define the current version of the slice
const SLICE_VERSION = '1.2';
const DEFAULT_LIST_VIEW_STATE = {
  currentPage: 1,
  shipmentsPerPage: 50,
};
const DEFAULT_STATUS_FILTER = "New";

// 🔹 Exported initial state for use in version-based merging
export const initialState = {
  _version: SLICE_VERSION,
  defaultTabs: [
    { id: 'shipment-list', name: 'Shipment List' },
    // You can uncomment or add more default tabs as needed
    // { id: 'shipment-form', name: 'Shipment Form' },
  ],

  dynamicTabs: [],
  activeTab: 0,
  statusFilter: DEFAULT_STATUS_FILTER,
  listViewState: {
    "shipment-list": { ...DEFAULT_LIST_VIEW_STATE },
  },
  advancedFilters: {}, 
};

// 🔹 Create the shipment slice
const shipmentSlice = createSlice({
  name: 'shipments',
  initialState,
  reducers: {
    addTab: (state, action) => {
      let tabDetails;

      if (action.payload) {
        tabDetails = action.payload;
      } else {
        const dynamicId =
          window.crypto?.randomUUID?.() ||
          `${Date.now()}-${Math.random().toString(36).slice(2, 15)}-${state.dynamicTabs.length}`;
        tabDetails = {
          id: `dyn-${dynamicId}`,
          name: `New Shipment ${state.dynamicTabs.length + 1}`,
          isDynamic: true,
          createdAt: Date.now(),
        };
      }

      pushDynamicTab(state, tabDetails, state.defaultTabs.length);
    },

    removeTab: (state, action) => {
      const { tabIndex } = action.payload;
      if (tabIndex < state.defaultTabs.length) return;

      const dynamicIndex = tabIndex - state.defaultTabs.length;
      state.dynamicTabs.splice(dynamicIndex, 1);

      // Always snap back to the first default tab (Shipment List) after closing any dynamic tab
      state.activeTab = 0;
    },

    setActiveTab: (state, action) => {
      const { index } = action.payload;
      if (index >= 0 && index < state.defaultTabs.length + state.dynamicTabs.length) {
        state.activeTab = index;
      }
    },

    resetTabs: (state) => {
      state.dynamicTabs = [];
      state.activeTab = 0;
    },
    setListViewState: (state, action) => {
      const { stateKey = "shipment-list", ...rest } = action.payload || {};
      const existing = state.listViewState[stateKey] || { ...DEFAULT_LIST_VIEW_STATE };
      state.listViewState[stateKey] = {
        ...existing,
        ...rest,
      };
    },
    setStatusFilter: (state, action) => {
      state.statusFilter = action.payload;
    },
    setDefaultTabs: (state, action) => {
      const next = (action.payload || []).filter(Boolean);
      if (!next.length) return;
      state.defaultTabs = next;
      const maxIndex = state.defaultTabs.length + state.dynamicTabs.length - 1;
      if (state.activeTab > maxIndex) {
        state.activeTab = Math.max(0, maxIndex);
      }
    },
    setAdvancedFilters: (state, action) => {
      const { stateKey, filters } = action.payload;
      state.advancedFilters[stateKey] = filters;
    },
    clearAdvancedFilters: (state, action) => {
      const { stateKey } = action.payload;
      delete state.advancedFilters[stateKey];
    },
  
  },

});

// 🔹 Export actions and reducer
export const {
  addTab,
  removeTab,
  setActiveTab,
  resetTabs,
  setListViewState,
  setStatusFilter,
  setDefaultTabs,
  setAdvancedFilters,
  clearAdvancedFilters,
} = shipmentSlice.actions;
export default shipmentSlice.reducer;
